import { ApiError, isApiError, toApiError } from "../data/errors";
import { errorText as accessErrorText } from "../access/errors";

/**
 * Refusal codes the team endpoints add to 5A's (`ACCESS_ERROR_CODES` already holds MEMBER_NOT_FOUND, ALREADY_MEMBER,
 * ACCESS_CHANGED, MANAGERS_OWNER_ONLY, NO_TEAM_IN_PERSONAL_ACCOUNT, OWNER_MUST_HAND_OVER, …). Each has a plain
 * sentence at `next.team.errors.<code lower-case>` in every language.
 */
export const TEAM_ERROR_CODES = ["OFFER_NOT_FOUND", "SHARE_NOT_ALLOWED", "ACCOUNT_CLOSED", "ACCOUNT_CANNOT_BE_ADDED", "ROLE_NOT_FOUND", "REPLACEMENT_REQUIRED", "REPLACEMENT_SAME_ROLE", "ROLE_HAS_NO_REACH", "VISITS_TOGETHER"] as const;
const TEAM_CODES = new Set<string>(TEAM_ERROR_CODES);

/** `team.errors.*` key for a team code; null for any other error. */
export const teamCodeKey = (code: string | undefined): string | null => (code && TEAM_CODES.has(code) ? `team.errors.${code.toLowerCase()}` : null);

type T = (key: string, opts?: Record<string, unknown>) => string;

/** The sentence to show for any thrown value: a team code first, then everything 5A already words (ACCESS_CHANGED names who). */
export const errorText = (err: unknown, t: T): string => {
  const e: ApiError = isApiError(err) ? err : toApiError(err);
  const key = teamCodeKey(e.code);
  return key ? t(key) : accessErrorText(e, t);
};

/** Who is already on the team, from a 409 ALREADY_MEMBER (`user_id`, `name`); null for any other error. */
export const alreadyMember = (err: unknown): { userId: number; name: string } | null => {
  const e = isApiError(err) ? err : toApiError(err);
  if (e.code !== "ALREADY_MEMBER") return null;
  const d = (e.raw as { response?: { data?: { user_id?: unknown; name?: unknown } } })?.response?.data;
  return typeof d?.user_id === "number" ? { userId: d.user_id, name: typeof d.name === "string" ? d.name : "" } : null;
};

/** Who changed a member's access in the meantime, from a 409 ACCESS_CHANGED (`changed_by`: {id, name, staff} or null; `changed_at`). */
export const changedBy = (err: unknown): { name: string | null; staff: boolean; at: string | null } | null => {
  const e = isApiError(err) ? err : toApiError(err);
  if (e.code !== "ACCESS_CHANGED") return null;
  const d = (e.raw as { response?: { data?: { changed_by?: { name?: unknown; staff?: unknown } | null; changed_at?: unknown } } })?.response?.data;
  const by = d?.changed_by;
  return {
    name: typeof by?.name === "string" && by.name.trim() ? by.name.trim() : null,
    staff: !!by?.staff,
    at: typeof d?.changed_at === "string" ? d.changed_at : null,
  };
};

/** Who to contact about a closed account, from a 409 ACCOUNT_CLOSED (`contact_name`: the Owner); null for any other error or no name. */
export const closedContact = (err: unknown): { name: string | null } | null => {
  const e = isApiError(err) ? err : toApiError(err);
  if (e.code !== "ACCOUNT_CLOSED") return null;
  const n = (e.raw as { response?: { data?: { contact_name?: unknown } } })?.response?.data?.contact_name;
  return { name: typeof n === "string" && n.trim() ? n.trim() : null };
};
